/**
 * Frontdesk-ops shared enums + response shapes (TASK-67).
 *
 * LOCAL FALLBACK: the backend agent is publishing these at
 * `packages/shared/src/types/frontdesk-ops.types.ts` (barrel-exported from
 * `@pms/shared`) in parallel. At the time this file was written that export was
 * not yet available, so per the build instructions we define matching local
 * enums/interfaces here (kept value-identical to 02-backend-design §2.5 so
 * FE/BE cannot drift). When `@pms/shared` ships these, callers can switch the
 * import; the string values are the contract.
 */

export enum MiscLedgerDirection {
  EXPENSE = 'expense',
  REVENUE = 'revenue',
}

export enum MiscLedgerCategory {
  PETTY_CASH = 'petty_cash',
  SUPPLIES = 'supplies',
  MAINTENANCE = 'maintenance',
  GUEST_SERVICE = 'guest_service',
  COMMISSION = 'commission',
  OTHER = 'other',
}

export enum HouseAccountItemDirection {
  CHARGE = 'charge',
  CREDIT = 'credit',
}

export enum LostFoundStatus {
  OPEN = 'open',
  CLAIMED = 'claimed',
  RETURNED = 'returned',
  DISPOSED = 'disposed',
}

// ─── Shift Summary (Flow 1) ──────────────────────────────────────────────────

export interface ShiftMethodRow {
  method: string;
  count: number;
  amount: number;
}

export interface ShiftSummaryTotals {
  collected: number;
  cash: number;
  check: number;
  cardOther: number;
  refunds: number;
  paidouts: number;
  net: number;
  /** Phase 2: sum of the day's cash drops. */
  cashDropped: number;
  /** Phase 2: cash − cashDropped (drops move cash, they are not revenue). */
  cashInDrawer: number;
}

export interface ShiftSummary {
  date: string;
  totals: ShiftSummaryTotals;
  byMethod: ShiftMethodRow[];
}

// ─── Cash Drops (Phase 2) ────────────────────────────────────────────────────

export interface CashDrop {
  id: string;
  /** DECIMAL(12,2) serialized as a string. */
  amount: string;
  note: string | null;
  dropDate: string;
  createdAt: string;
  userId: string;
  /** Recorder's display name when joinable, else null (falls back to userId). */
  recordedByName: string | null;
}

export interface CashDropList {
  drops: CashDrop[];
  totalDropped: number;
}

// ─── Misc Ledger (Flows 2/3) ─────────────────────────────────────────────────

export interface MiscLedgerEntry {
  id: string;
  propertyId: string;
  tenantId: string;
  direction: MiscLedgerDirection;
  category: MiscLedgerCategory;
  /** DECIMAL string, e.g. "12.50" */
  amount: string;
  note: string | null;
  entryDate: string;
  createdBy: string | null;
  createdByName?: string | null;
  createdAt: string;
}

// ─── House Accounts (Flow 4) ─────────────────────────────────────────────────

export interface HouseAccountWithBalance {
  id: string;
  propertyId: string;
  tenantId: string;
  name: string;
  description: string | null;
  /** DECIMAL string */
  balance: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HouseAccountItem {
  id: string;
  houseAccountId: string;
  direction: HouseAccountItemDirection;
  /** DECIMAL string */
  amount: string;
  description: string;
  entryDate: string;
  postedBy: string | null;
  postedByName?: string | null;
  createdAt: string;
}

export interface HouseAccountDetail {
  account: HouseAccountWithBalance;
  items: HouseAccountItem[];
}

// ─── House Inventory (Flow 5) ────────────────────────────────────────────────

export interface HouseInventoryCell {
  date: string;
  total: number;
  available: number;
  reserved: number;
  ooo: number;
  maintenanceRequestIds?: string[];
}

export interface HouseInventoryRoomType {
  id: string;
  name: string;
  code?: string;
  dates: HouseInventoryCell[];
}

export interface HouseInventoryGrid {
  roomTypes: HouseInventoryRoomType[];
  dates: string[];
}

// ─── Lost & Found (Flow 6) ───────────────────────────────────────────────────

export interface LostFoundLinkedBooking {
  id: string;
  guestName?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
}

export interface LostFoundItem {
  id: string;
  propertyId: string;
  tenantId: string;
  description: string;
  foundDate: string;
  location: string;
  guestName: string | null;
  guestMobile: string | null;
  status: LostFoundStatus;
  bookingId: string | null;
  booking?: LostFoundLinkedBooking | null;
  notes: string | null;
  loggedBy: string | null;
  loggedByName?: string | null;
  createdAt: string;
  updatedAt: string;
}
